import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, LogOut } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

function initialsFrom(email: string): string {
  const name = email.split("@")[0] ?? "";
  const parts = name.split(/[._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0] ?? "");
  return (letters.join("") || name.slice(0, 2) || "?").toUpperCase();
}

/**
 * Who is signed in, at the foot of the sidebar.
 *
 * Shown in full rather than as initials in a corner: call-centre desks are
 * shared, and "whose session is this?" should be answerable at a glance. In
 * the icon rail it shrinks to the initials, and the menu still names them.
 */
export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  if (user === null) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      toast.success("Signed out");
      void navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const avatar = (
    <span
      aria-hidden="true"
      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sidebar-active text-xs font-medium text-sidebar-foreground"
    >
      {initialsFrom(user.email)}
    </span>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Account: ${user.email}`}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-sidebar-active/60 touch:p-2.5",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed && "justify-center",
        )}
      >
        {avatar}
        {collapsed ? null : (
          <>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{user.email}</span>
              {/* Role is free text from ad_admin.role, shown as-is. */}
              {user.role !== "" ? (
                <span className="truncate text-xs text-muted-foreground">
                  {user.role}
                </span>
              ) : null}
            </span>
            <ChevronsUpDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
          <span className="truncate text-sm font-medium">{user.email}</span>
          {user.role !== "" ? (
            <span className="truncate text-xs text-muted-foreground">{user.role}</span>
          ) : null}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          destructive
          disabled={isLoggingOut}
          onSelect={(event) => {
            // Keep the menu mounted while the request is in flight.
            event.preventDefault();
            void handleLogout();
          }}
        >
          <LogOut aria-hidden="true" />
          {isLoggingOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
